import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const PURCHASABLE_PLANS = new Set(['starter', 'pro', 'elite', 'ultimate']);

function normalizeBilling(raw: string | null): 'monthly' | 'yearly' {
  const v = (raw || 'monthly').toLowerCase();
  if (v === 'yearly' || v === 'annual' || v === 'year') return 'yearly';
  return 'monthly';
}

const LoginForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSignup = searchParams.get('signup') === '1' || searchParams.get('signup') === 'true';
  const planParam = (searchParams.get('plan') || '').toLowerCase();
  const plan = PURCHASABLE_PLANS.has(planParam) ? planParam : '';
  const billing = normalizeBilling(searchParams.get('billing'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(initialSignup);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { login, register, error, isLoading } = useAuth();
  const navigate = useNavigate();

  const signupHint = useMemo(() => {
    if (!isRegistering) return null;
    if (plan) {
      return `After you create your account, we’ll send you to choose personal or business use, then Stripe checkout for the ${plan} plan (${billing}). You’ll add a card and start a 7-day trial — you are not charged until the trial ends.`;
    }
    return 'After you create your account, you’ll choose a plan and personal or business use, then add a card on Stripe. You’ll start a 7-day trial and are not charged until the trial ends.';
  }, [isRegistering, plan, billing]);

  const goToCheckout = () => {
    const params = new URLSearchParams();
    params.set('checkout', '1');
    if (plan) params.set('plan', plan);
    params.set('billing', billing);
    navigate(`/settings?${params.toString()}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (isRegistering) {
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match.');
        return;
      }
      const result = await register({ email, password, firstName, lastName });
      if (result.success) {
        goToCheckout();
      }
    } else {
      const result = await login(email, password);
      if (result.success) {
        if (searchParams.get('checkout') === '1' || plan) {
          goToCheckout();
        } else {
          navigate('/');
        }
      }
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL || ''}/BranchRouterLogo.jpg`}
              alt="Branch Router logo"
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                mb: 2,
                display: 'block',
                mx: 'auto',
              }}
            />
            <Typography component="h1" variant="h4" gutterBottom>
              Branch Router
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {isRegistering ? 'Create your account' : 'Sign in to your account'}
            </Typography>
            {signupHint && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {signupHint}
              </Typography>
            )}
          </Box>

          {(localError || error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {localError || error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {isRegistering && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  autoComplete="given-name"
                  autoFocus
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isLoading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isLoading}
                />
              </>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus={!isRegistering}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete={isRegistering ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            {isRegistering && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                error={password !== confirmPassword && confirmPassword !== ''}
                helperText={
                  password !== confirmPassword && confirmPassword !== ''
                    ? 'Passwords do not match'
                    : ''
                }
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : isRegistering ? (
                plan ? 'Create account & continue to trial' : 'Create account'
              ) : (
                'Sign In'
              )}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setLocalError(null);
                setIsRegistering(!isRegistering);
              }}
              disabled={isLoading}
            >
              {isRegistering ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
            </Button>
            {!isRegistering && (
              <Button
                fullWidth
                variant="text"
                component={RouterLink}
                to="/forgot-password"
                disabled={isLoading}
              >
                Forgot your password?
              </Button>
            )}
          </Box>
        </Paper>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 3, maxWidth: 420 }}
        >
          Support:{' '}
          <a href="mailto:support@branchrouter.com">support@branchrouter.com</a>
          <br />
          <a href="https://branchrouter.com/privacy.html" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          {' · '}
          <a href="https://branchrouter.com/terms.html" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default LoginForm;
